import React, { useState, useCallback, useRef, Fragment } from "react";
import styles from "./upload_section.module.css";
import { useDropzone } from "react-dropzone";
import getStringsBasedLang from "../../strings/get_strings";
import CustomPrimaryButton from "../../modules/custom_primary_button/custom_primary_button";
import { LinearProgress } from "@material-ui/core";
import SelectAvModal from "../select_av_modal/select_av_modal";
import { useNavigate } from "react-router-dom";
import CryptoMD5 from "crypto-js/md5";
import CryptoLatin1Encoder from "crypto-js/enc-latin1";
import ConfirmScanModal from "./confirm_scan_modal";
import { createData } from "../../api/http-service";
import UseErrorHandling, { showToastError } from "../../hook/UseErrorHandling";
import { Input, Radio } from "antd";
import { DOMAIN_REGEX } from "../../utils";
import { toast } from "react-toastify";
import PasswordProtectModal from "../password_protect_modal/password_protect_modal";

export default function UploadSection(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState('file');
  const [guid, setGuid] = useState('');
  const [path, setPath] = useState('');
  const [domain, setDomain] = useState('');
  const [progress, setProgress] = useState(0);
  const childRef = useRef();
  const passwordProtectModalRef = useRef();
  const confrimModalRef = useRef();
  let navigate = useNavigate();
  let token = localStorage.getItem("token");
  const handleErrorApi = UseErrorHandling();
  const lang = localStorage.getItem("lang")

  const showAvModalHandler = async (file) => {
    if (!token) {
      navigate("/login");
      return;
    }

    const reader = new FileReader();
    reader.readAsBinaryString(file);

    reader.onloadend = (e) => {
      const gotMD5 = CryptoMD5(CryptoLatin1Encoder.parse(e.target.result));
      const MD5 = gotMD5.toString();
      createData(
        "scan/search",
        {
          token: token,
          hash: MD5,
        }
      )
        .then(async (getLastScanListResult) => {
          if (!getLastScanListResult?.success) {
            setIsLoading(false);
            handleErrorApi(getLastScanListResult);
            return;
          }
          if (getLastScanListResult.data.scans.length > 0) {
            let callback = await confrimModalRef.current.showModal();
            if (callback === "yes") await doScan(file, MD5, false);
            else
              navigate(`/results?hash=${MD5}&scanid=${getLastScanListResult.data.scans[0].guid}`)
          } else {
            doScan(file, MD5, false);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          if (error?.response) {
            handleErrorApi(error?.response.data);
          }
        });
    };
  };

  const checkDomain = () => {
    if (!DOMAIN_REGEX.test(domain)) {
      toast.warning(getStringsBasedLang('selectFile_validDomain'))
      return
    }
    doScan('', '', true)
  }

  const doScan = async (file, md5, is_web) => {
    setIsLoading(true);
    let callbackData = await childRef.current.showAlert();
    if (callbackData) {
      const avs = callbackData.join(" ")
      let data = {}
      const formData = new FormData();
      if (!is_web) {
        formData.append("token", token);
        formData.append("file", file);
        formData.append("avs", avs);
      } else {
        data["token"] = token
        data["autostart"] = false
        data["urls"] = [domain]
        data["avs"] = avs
      }

      await createData(!is_web ? "scan/multiscanner/init" : "scan/url/init", !is_web ? formData : data,
        undefined,
        undefined,
        (progressEvent) => {
          const { loaded, total } = progressEvent;
          let percentCompleted = Math.floor((loaded * 100) / total);
          if (!is_web)
            setProgress(percentCompleted);
        })
        .then(async (scanInitResult) => {
          if (!scanInitResult?.success) {
            setIsLoading(false);
            handleErrorApi(scanInitResult);
            return;
          }
          const guid = scanInitResult.guid;
          setGuid(guid)
          setPath(file.path)
          const password_protected = scanInitResult.password_protected;
          if (password_protected.length > 0) {
            let protectCallback = await passwordProtectModalRef.current.showProtectModal();
            if (!protectCallback) {
              setIsLoading(false);
              setProgress(0)
              return
            }
          }
          createData(`scan/start/${guid}`, { token: token })
            .then((scanStartResult) => {
              if (!scanStartResult?.success) {
                setIsLoading(false);
                setProgress(0)
                handleErrorApi(scanStartResult);
                return;
              }
              navigate(`/results?hash=${md5}&scanid=${guid}`);
              setTimeout(() => { }, 500);
            })
            .catch((error) => {
              setProgress(0)
              setIsLoading(false);
              if (error?.response) {
                handleErrorApi(error?.response.data);
              }
            });
        })
        .catch((error) => {
          setIsLoading(false);
          if (error?.response) {
            handleErrorApi(error?.response.data);
          }
        });
    } else {
      setIsLoading(false);
      setProgress(0)
      showToastError(getStringsBasedLang("general_stopOperaion"));
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    showAvModalHandler(acceptedFiles[0]);
    setIsLoading(true);

  });

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: false,
    disabled: mode === "file" ? false : true
  });

  return (
    <div className={styles.container}>
      <div className={styles.file_input_text_container}>
        <h1 className={styles.file_input_text}>
          {getStringsBasedLang("selectFile_inputText1")}
        </h1>
        <h1
          className={`${styles.file_input_text} ${styles.file_input_text_bottom}`}
        >
          {getStringsBasedLang("selectFile_inputText2")}
        </h1>
        <div className={styles.malware_count_container}>
          <div className={styles.file_input_container}>
            <div className={styles.box_container}>
              <div className={styles.shape_bottom} />

              <div className={styles.file_input} {...getRootProps()}>
                {progress ? (
                  <div className={`mt-10 text-xl text-center ${!lang || lang === 'en' ? 'englishIranSans' : ''}`}> {progress}%</div>
                ) : (
                  <div className="h-32">
                    {
                      mode === "file" && <input {...getInputProps()} />
                    }
                    <div className="mt-2">
                      <Radio.Group onChange={(e) => setMode(e.target.value)} value={mode}>
                        <Radio.Button value="file">{getStringsBasedLang('selectFile_file')}</Radio.Button>
                        <Radio.Button value="web">{getStringsBasedLang('selectFile_web')}</Radio.Button>
                      </Radio.Group>
                    </div>
                    {
                      mode === "file" ?
                      
                        <React.Fragment>
                          <div className="flex justify-center">
                            <img
                              alt="cloudFileSelect"
                              src={
                                require("../../assets/icons/cloud_file_select.svg")
                                  .default
                              }
                            />
                          </div>
                          <div className={styles.dropzone_title}>
                            {getStringsBasedLang("selectFile_dropZonTitle")}
                          </div>
                        </React.Fragment>
                        :
                        <Input size="small" onChange={(e) => setDomain(e.target.value)} className="w-1/2 mt-10" placeholder={getStringsBasedLang('selectFile_enterDomain')} />
                    }
                  </div>
                )}
                <div>
                  {!isLoading ? (
                    <React.Fragment>
                      <CustomPrimaryButton
                        doClick={() => mode !== "file" ? checkDomain() : (token ? open() : navigate("/login"))}
                        text={getStringsBasedLang(mode === "file" ? "selectFile_chooseFile" : "selectFile_start")}
                      />
                    </React.Fragment>
                  ) : (
                    <div className={styles.prog_container}>
                      <LinearProgress />
                      <div className="pt-2">
                        <span className="primary_text_color font-bold">
                          {getStringsBasedLang("please_wait")}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SelectAvModal ref={childRef} />
      <PasswordProtectModal ref={passwordProtectModalRef} guid={guid} path={path} />
      <ConfirmScanModal ref={confrimModalRef} />
    </div>
  );
}
